import eventBus from '@/lib/event_bus';
import { get, isEmpty } from '@/lib/helpers';
import { getNavigationRoute, updateLayoutConfig } from '@/lib/layout_config';
import { layoutConfig } from '@/lib/layout_consts';
import { isCustomerAuthenticatedSync } from '@/lib/login_utils';
import { isMicroappsEnabled } from '@/lib/utils';
import { retrieveBigCookie } from '@/store/actions/customer';
import { authState } from '@/store/getters/auth';

const beforeEnter = async (to: any, from: any, next: any) => {
  if (isMicroappsEnabled()) {
    updateLayoutConfig('GPAY_LAYOUT');
  } else {
    updateLayoutConfig('DEFAULT_LAYOUT');
  }
  try {
    eventBus.$emit('loader', {
      show: true,
      message: 'Loading...',
    });
    let navigationRoute;
    if (to && to.name) {
      navigationRoute = getNavigationRoute(undefined, undefined, to.name);
    } else {
      navigationRoute = getNavigationRoute();
    }
    const { targetRoute = '', query = {}, params = {} } = navigationRoute;
    if (targetRoute && targetRoute !== to.name) {
      if (isCustomerAuthenticatedSync(authState())) {
        await retrieveBigCookie(false, 'gpayRoutes:beforeEnter');
      }
      next({
        name: targetRoute,
        query: !isEmpty(query) ? query : undefined,
        params: !isEmpty(params) ? params : undefined,
      });
    } else {
      next();
    }
  } catch (err) {
    console.log('Error while getting customer details', err);
    next();
  } finally {
    eventBus.$emit('loader');
  }
};

export const gpayRoutes = {
  path: '/go',
  name: get(layoutConfig, 'GPAY_LAYOUT.routes.aipHome', ''),
  component: () =>
    import(
      /* webpackChunkName: 'gp-app-layout' */ '@/layouts/GpayAppLayout.vue'
    ),
  meta: { requiresAuth: true },
  beforeEnter,
  children: [
    {
      path: 'benefits',
      name: get(layoutConfig, 'GPAY_LAYOUT.routes.benefits', ''),
      component: () =>
        import(
          /* webpackChunkName: "gp-benefits" */ '@/views/BenefitsList.vue'
        ),
    },
    {
      path: 'beneficiary-details',
      name: get(layoutConfig, 'GPAY_LAYOUT.routes.beneficiaryDetails', ''),
      props: true,
      component: () =>
        import(
          /* webpackChunkName: "gp-bene-details" */ '@/views/BeneficiaryDetails.vue'
        ),
    },
    {
      path: 'upload-documents',
      name: get(layoutConfig, 'GPAY_LAYOUT.routes.uploadDocuments', ''),
      props: true,
      component: () =>
        import(
          /* webpackChunkName: "gp-upload-docs" */ '@/views/UploadDocuments.vue'
        ),
    },
    {
      path: 'bank-formalities',
      name: get(layoutConfig, 'GPAY_LAYOUT.routes.bankFormalities', ''),
      props: true,
      component: () =>
        import(
          /* webpackChunkName: "gp-bank-formals" */ '@/views/BankFormalities.vue'
        ),
    },
    {
      path: 'office-address',
      name: get(layoutConfig, 'GPAY_LAYOUT.routes.officeAddress', ''),
      props: true,
      component: () =>
        import(
          /* webpackChunkName: "gp-off-addr" */ '@/views/OfficeAddress.vue'
        ),
    },
    {
      path: 'terms-and-conditions',
      name: get(layoutConfig, 'GPAY_LAYOUT.routes.termsAndConditions', ''),
      props: true,
      component: () =>
        import(
          /* webpackChunkName: "gp-tnc" */ '@/views/TermsAndConditions.vue'
        ),
    },
    {
      path: 'sign-documents-dmi',
      name: get(layoutConfig, 'GPAY_LAYOUT.routes.signDocumentsDmi', ''),
      props: true,
      component: () =>
        import(
          /* webpackChunkName: "gp-sign-dmi" */ '@/views/signDocuments/dmi/SignDocuments.vue'
        ),
    },
    {
      path: 'sign-documents-rbl',
      name: get(layoutConfig, 'GPAY_LAYOUT.routes.signDocumentsRbl', ''),
      props: true,
      component: () =>
        import(
          /* webpackChunkName: "gp-sign-rbl" */ '@/views/signDocuments/rbl/SignDocuments.vue'
        ),
    },
    {
      path: 'sign-documents-muskmelon',
      name: get(layoutConfig, 'GPAY_LAYOUT.routes.signDocumentsMuskmelon', ''),
      props: true,
      component: () =>
        import(
          /* webpackChunkName: "gp-sign-mm" */
          '@/views/signDocuments/muskmelon/SignDocuments.vue'
        ),
    },
    {
      path: 'sign-documents-incred',
      name: get(layoutConfig, 'GPAY_LAYOUT.routes.signDocumentsIncred', ''),
      props: true,
      component: () =>
        import(
          /* webpackChunkName: "gp-sign-incred" */
          '@/views/signDocuments/incred/SignDocuments.vue'
        ),
    },
    {
      path: 'sign-documents-fullerton',
      name: get(layoutConfig, 'GPAY_LAYOUT.routes.signDocumentsFullerton', ''),
      props: true,
      component: () =>
        import(
          /* webpackChunkName: "gp-sign-fullerton" */
          '@/views/signDocuments/fullerton/SignDocuments.vue'
        ),
    },
    {
      path: 'sign-documents-tapstart',
      name: get(layoutConfig, 'GPAY_LAYOUT.routes.signDocumentsTapstart', ''),
      props: true,
      component: () =>
        import(
          /* webpackChunkName: "gp-sign-tapstart" */ '@/views/signDocuments/tapstart/SignDocuments.vue'
        ),
    },
    {
      path: 'sign-documents-lendbox',
      name: get(layoutConfig, 'GPAY_LAYOUT.routes.signDocumentsLendbox', ''),
      props: true,
      component: () =>
        import(
          /* webpackChunkName: "gp-sign-lendbox" */ '@/views/signDocuments/lendbox/SignDocuments.vue'
        ),
    },
    {
      path: 'sign-documents-cholamandalam',
      name: get(
        layoutConfig,
        'GPAY_LAYOUT.routes.signDocumentsCholamandalam',
        ''
      ),
      props: true,
      component: () =>
        import(
          /* webpackChunkName: "gp-sign-cholamandalam" */ '@/views/signDocuments/cholamandalam/SignDocuments.vue'
        ),
    },
    {
      path: 'sign-documents-creditsaison',
      name: get(
        layoutConfig,
        'GPAY_LAYOUT.routes.signDocumentsCreditSaison',
        ''
      ),
      props: true,
      component: () =>
        import(
          /* webpackChunkName: "gp-sign-cs" */
          '@/views/signDocuments/credit_saison/SignDocuments.vue'
        ),
    },
    {
      path: 'sign-documents-hdbfs',
      name: get(layoutConfig, 'GPAY_LAYOUT.routes.signDocumentsHdbfs', ''),
      props: true,
      component: () =>
        import(
          /* webpackChunkName: "gp-sign-hdbfs" */
          '@/views/signDocuments/hdbfs/SignDocuments.vue'
        ),
    },
    {
      path: 'interest-rate',
      name: get(layoutConfig, 'GPAY_LAYOUT.routes.interestRate', ''),
      props: true,
      component: () =>
        import(
          /* webpackChunkName: "gp-int-calc" */ '@/views/InterestRate.vue'
        ),
    },
    {
      path: 'application-status-tracker',
      name: get(layoutConfig, 'GPAY_LAYOUT.routes.appStatusTracker', ''),
      component: () =>
        import(
          /* webpackChunkName: "ir" */ '@/views/ApplicationStatusTracker.vue'
        ),
    },
    {
      path: 'e-mandate',
      name: get(layoutConfig, 'GPAY_LAYOUT.routes.emandate', ''),
      component: () =>
        import(
          /* webpackChunkName: "gp-mandate" */ '@/views/SetupEmandate.vue'
        ),
    },
    {
      path: 'okyc',
      name: get(layoutConfig, 'GPAY_LAYOUT.routes.okycOld', ''),
      props: true,
      component: () =>
        import(
          /* webpackChunkName: "gp-okyc-veri5" */ '@/views/OfflineKyc.vue'
        ),
    },
    {
      path: 'okyc/veri5',
      name: get(layoutConfig, 'GPAY_LAYOUT.routes.okyc', ''),
      props: true,
      component: () =>
        import(
          /* webpackChunkName: "gp-okyc-veri5" */ '@/views/OfflineKyc.vue'
        ),
    },
    {
      path: 'okyc/hyprvrg',
      name: get(layoutConfig, 'GPAY_LAYOUT.routes.hypervergeOkyc', ''),
      props: true,
      component: () =>
        import(
          /* webpackChunkName: "gp-hyperverge-okyc" */ '@/views/HypervergeOkyc.vue'
        ),
    },
    {
      path: 'complete-kyc',
      name: get(layoutConfig, 'GPAY_LAYOUT.routes.completeKyc', ''),
      props: true,
      component: () =>
        import(
          /* webpackChunkName: "gp-complete-kyc-landing" */ '@/views/CompleteKycLanding.vue'
        ),
    },
    {
      path: 'upload-salary-slips',
      name: get(layoutConfig, 'GPAY_LAYOUT.routes.uploadSalarySlips', ''),
      props: true,
      component: () =>
        import(
          /* webpackChunkName: "gp-upload-salary-slips" */ '@/views/UploadSalarySlips.vue'
        ),
    },
    {
      path: 'schedule-appointment',
      name: get(layoutConfig, 'GPAY_LAYOUT.routes.scheduleAppointment', ''),
      component: () =>
        import(
          /* webpackChunkName: "gp-schedule-appointment" */ '@/views/ScheduleAppointment.vue'
        ),
    },
    {
      path: 'upload-selfie',
      name: get(layoutConfig, 'GPAY_LAYOUT.routes.uploadSelfie', ''),
      props: true,
      component: () =>
        import(
          /* webpackChunkName: "gp-upload-selfie" */ '@/views/UploadSelfie.vue'
        ),
    },
    {
      path: 'dkyc',
      name: get(layoutConfig, 'GPAY_LAYOUT.routes.dkyc', ''),
      props: true,
      component: () =>
        import(/* webpackChunkName: "gp-dkyc" */ '@/views/DigiKyc.vue'),
    },
  ],
};
